// Tabella articoli del PDF del preventivo. Unica per lista, creazione e
// modifica: ogni pagina prepara le sue righe, colonne e formati stanno qui.
//
// Listino e scontato sono i prezzi imponibili fotografati sulla riga; poi
// l'aliquota della riga e l'importo ivato (`quote_items.price` × quantita').
// Il riepilogo divide l'IVA per aliquota con la sua base: la colonna IVA
// serve a capire da quali righe viene ogni base.

import { eur } from "./pdfRiepilogo";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const INTESTAZIONE_ARTICOLI = [
  "Cod.",
  "Descrizione",
  "U.m.",
  "Q.tà",
  "Prezzo listino",
  "Prezzo scontato",
  "IVA",
  "Importo ivato",
];

export const NOTA_PREZZI = "Prezzo listino e prezzo scontato IVA esclusa";

/** "€ 1.234,50" (formato italiano, come il riepilogo), oppure "—" se il
 *  prezzo non c'e' (righe di prima, niente sconto). */
/** Quantita' in formato italiano: "1,44", "3". */
export function quantitaCella(v) {
  const n = round2(Number(v) || 0);
  return String(n).replace(".", ",");
}

export function prezzoCella(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `€ ${eur(n)}` : "—";
}

/**
 * Imponibile e IVA delle righe divisi per aliquota:
 * { 22: { imponibile: 80, iva: 17.6 }, 0: { imponibile: 104.1, iva: 0 } }.
 * Le righe hanno importo IVA compresa ({ lineTotal, iva }).
 */
export function ivaPerAliquota(righe) {
  const perAliquota = {};
  (righe || []).forEach(({ lineTotal, iva }) => {
    const lordo = Number(lineTotal) || 0;
    const aliquota = Number(iva ?? 22);
    const imposta = lordo - lordo / (1 + aliquota / 100);
    const voce = perAliquota[aliquota] || { imponibile: 0, iva: 0 };
    voce.imponibile += lordo - imposta;
    voce.iva += imposta;
    perAliquota[aliquota] = voce;
  });
  Object.values(perAliquota).forEach((voce) => {
    voce.imponibile = round2(voce.imponibile);
    voce.iva = round2(voce.iva);
  });
  return perAliquota;
}

// Colonne della tabella: `campo` è la chiave della finestra di stampa
// (campiStampa.js); descrizione e quantità ci sono sempre [sc-49299].
const COLONNE_ARTICOLI = [
  { campo: "colCodice", titolo: "Cod.", larghezza: 0.1, halign: "left" },
  { titolo: "Descrizione", larghezza: null, halign: "left" },
  { campo: "colUm", titolo: "U.m.", larghezza: 0.06, halign: "center" },
  { titolo: "Q.tà", larghezza: 0.07, halign: "right" },
  {
    campo: "colListino",
    titolo: "Prezzo listino",
    larghezza: 0.12,
    halign: "right",
  },
  {
    campo: "colScontato",
    titolo: "Prezzo scontato",
    larghezza: 0.12,
    halign: "right",
  },
  { campo: "colIva", titolo: "IVA", larghezza: 0.07, halign: "right" },
  {
    campo: "colImporto",
    titolo: "Importo ivato",
    larghezza: 0.14,
    halign: "right",
  },
];

/**
 * Costruisce la tabella per jspdf-autotable.
 *
 * `righe`: [{ group, code, desc, unit, qty, listPrice, discountedPrice,
 *             unitPrice, iva }], nell'ordine in cui vanno stampate; i gruppi
 * (categorie) escono nell'ordine in cui compaiono.
 * `campi`: colonne da stampare (finestra di stampa); senza, tutte.
 */
export function tabellaArticoli(righe, { pageWidth, marginX, campi } = {}) {
  const colonne = COLONNE_ARTICOLI.filter(
    (c) => !c.campo || !campi || campi[c.campo] !== false
  );
  const numeroColonne = colonne.length;
  const order = [];
  const groups = {};
  (righe || []).forEach((r) => {
    const g = r.group || "GENERALE";
    if (!groups[g]) {
      groups[g] = [];
      order.push(g);
    }
    groups[g].push(r);
  });

  const body = [];
  const righeIva = [];
  order.forEach((g) => {
    body.push([
      {
        content: g,
        colSpan: numeroColonne,
        styles: {
          fillColor: [235, 235, 235],
          fontStyle: "bold",
          halign: "left",
        },
      },
    ]);
    groups[g].forEach((r) => {
      const unitPrice = Number(r.unitPrice) || 0;
      const qty = Number(r.qty) || 0;
      const lineTotal = round2(unitPrice * qty);
      righeIva.push({ lineTotal, iva: r.iva ?? 22 });
      const celle = {
        "Cod.": r.code || "-",
        Descrizione: r.desc || "-",
        "U.m.": r.unit,
        "Q.tà": quantitaCella(qty),
        "Prezzo listino": prezzoCella(r.listPrice),
        "Prezzo scontato": prezzoCella(r.discountedPrice),
        IVA: `${Number(r.iva ?? 22)}%`,
        "Importo ivato": prezzoCella(lineTotal),
      };
      body.push(colonne.map((c) => celle[c.titolo]));
    });
  });

  // Larghezze: la descrizione prende quello che resta
  const usable = pageWidth - 2 * marginX;
  const w = (f) => Math.floor(usable * f);
  const columnStyles = {};
  colonne.forEach((c, i) => {
    columnStyles[i] =
      c.larghezza === null
        ? { cellWidth: "auto", minCellWidth: 30, halign: c.halign }
        : { cellWidth: w(c.larghezza), halign: c.halign };
  });
  // la nota sui prezzi serve solo se c'è almeno uno dei due prezzi
  const conPrezzi = colonne.some(
    (c) => c.campo === "colListino" || c.campo === "colScontato"
  );

  return {
    head: [colonne.map((c) => c.titolo)],
    body,
    foot: conPrezzi
      ? [
          [
            {
              content: NOTA_PREZZI,
              colSpan: numeroColonne,
              styles: {
                fontSize: 8,
                fontStyle: "italic",
                halign: "left",
                textColor: [100, 100, 100],
                fillColor: [255, 255, 255],
              },
            },
          ],
        ]
      : [],
    ivaPerAliquota: ivaPerAliquota(righeIva),
    tableWidth: usable,
    margin: { left: marginX, right: marginX },
    columnStyles,
  };
}
